import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { viewerApi, ViewerMeResponse } from 'app/services/edpApiV1/viewer';
import { TDivisionEmployees, TModerators } from 'shared/types/types';

type ViewerMe = {
  id: number | null;
  email: string | null;
  name: string | null;
  middleName: string | null;
  lastName: string;
  division?: {
    id: string;
    name: string;
    code: string;
  };
  actingDivisions?: {
    id: string;
    name: string;
  }[];
  employee: {
    id: number | null;
    name: string | null;
    middleName: string | null;
    lastName: string;
    personnelNumber: string | null;
    division: string | null;
    position: string | null;
    organization: string | null;
  };
  organization: { id: string; name: string | null };
  role: string | null;
  permissions: string[];
  moderators?: TModerators;
  dublicate?: {
    actingDivisions: { id: string; name: string }[];
    deputyMe?: {
      employee: TDivisionEmployees;
      id: number;
      supervisor: TDivisionEmployees;
      validFrom: string;
      validTo: string;
    }[];
    deputyI?: {
      divisionId: string;
      divisionName: string;
      employee: TDivisionEmployees;
      id: number;
      supervisor: TDivisionEmployees;
      validFrom: string;
      validTo: string;
    }[];
    isActing: boolean;
    isDeputy: boolean;
    isTimekeeper: boolean;
    isSupervisor: boolean;
    isSupervisorInDivision: boolean;
    isSupervisorYourDivision: boolean;
  };
};
const initialState = {
  id: null,
  email: null,
  name: '',
  middleName: '',
  lastName: '',
  employee: {
    id: null,
    name: '',
    middleName: '',
    lastName: '',
    personnelNumber: null,
    division: null,
    position: null,
    organization: null,
  },
  organization: { id: '', name: null },
  role: '',
  permissions: [''],
} as ViewerMe;

export const viewerMeSlice = createSlice({
  name: 'viewer/me',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addMatcher(
        viewerApi.endpoints.me.matchFulfilled,
        (state, { payload }: PayloadAction<ViewerMeResponse>) => payload,
      )
      .addMatcher(viewerApi.endpoints.me.matchRejected, () => initialState);
  },
});

const selectViewerProfile = (state: RootState) => state.entities.viewer.me;
const selectModerators = (state: RootState) => state.entities.viewer.me.moderators;
const selectDublicate = (state: RootState) => state.entities.viewer.me.dublicate;
const getEmail = createSelector(selectViewerProfile, ({ email }) => email);
const meId = createSelector(selectViewerProfile, ({ id }) => id);
const getMeOrganizationId = createSelector(selectViewerProfile, ({ organization }) => organization.id);
const getMyPermissions = createSelector(selectViewerProfile, ({ permissions }) => permissions);
const getViewerProfileInfo = createSelector(selectViewerProfile, ({ employee }) => employee);

export const selectors = {
  selectViewerProfile,
  selectModerators,
  selectDublicate,
  getEmail,
  getMeOrganizationId,
  meId,
  getMyPermissions,
  getViewerProfileInfo,
};
export const actions = viewerMeSlice.actions;
export const reducer = viewerMeSlice.reducer;
